/**
 * Naming a piece of a shape.
 *
 * After a cut, a piece is some fraction of what it came from. A child cutting
 * roughly down the middle has not made exactly one half, so the label has to
 * be forgiving enough to say "about a half" when that is plainly what happened,
 * and honest enough to fall back to a percentage when it is not.
 */

export interface Fraction {
  numerator: number;
  denominator: number;
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

export function simplify({ numerator, denominator }: Fraction): Fraction {
  const d = gcd(Math.abs(numerator), Math.abs(denominator)) || 1;
  return { numerator: numerator / d, denominator: denominator / d };
}

/**
 * Denominators a child has a word for. Allowing everything up to twelfths
 * sounds more precise and is in fact worse: almost any rough cut lands within
 * a whisker of some twelfth, so the label would confidently announce "5/12"
 * about a piece the child hacked off by eye.
 */
const DENOMINATORS = [2, 3, 4, 5, 6, 8];

/**
 * The tidiest fraction within `tolerance` of a share, or null when nothing
 * simple is close enough — in which case the toy says a percentage instead of
 * pretending the cut was neater than it was.
 */
export function nearestSimple(share: number, tolerance = 0.03): Fraction | null {
  if (!Number.isFinite(share) || share <= 0 || share >= 1) return null;
  let best: Fraction | null = null;
  let bestError = Infinity;

  for (const denominator of DENOMINATORS) {
    const numerator = Math.round(share * denominator);
    if (numerator <= 0 || numerator >= denominator) continue;
    const error = Math.abs(share - numerator / denominator);
    const simple = simplify({ numerator, denominator });
    // Prefer a smaller denominator when two are equally close.
    if (error < bestError - 1e-9 || (Math.abs(error - bestError) < 1e-9 && best && simple.denominator < best.denominator)) {
      best = simple;
      bestError = error;
    }
  }
  return best && bestError <= tolerance ? best : null;
}

const NAMES: Record<string, string> = {
  '1/2': 'a half',
  '1/3': 'a third',
  '2/3': 'two thirds',
  '1/4': 'a quarter',
  '3/4': 'three quarters',
  '1/5': 'a fifth',
  '1/6': 'a sixth',
  '1/8': 'an eighth',
};

/** What to write on the piece. */
export function shareLabel(share: number): string {
  const simple = nearestSimple(share);
  if (!simple) return `${Math.round(share * 100)}%`;
  const key = `${simple.numerator}/${simple.denominator}`;
  return NAMES[key] ?? key;
}
